import * as React from "react";
import { MobXProviderContext, observer } from "mobx-react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { MUIAdornment, MUIBlock, MUIFilter, MUISelect } from "../../../MUI";
import * as styles from "./styles.scss";
import cx from "classnames";
import { tl } from "../../../../../utils";

import { SvgIcon } from "../../../../../components";

const sortOptionsList = [
  { value: "added", label: tl("По дате добавления") },
  { value: "size", label: tl("По размеру") },
  { value: "name", label: tl("По алфавиту") },
];

let throttleTO: any;

interface IProps {
  key: any;
  style: React.CSSProperties;
  isStuck: boolean;
}

export const FiltersPanel = observer((props: IProps) => {
  const { fsStore, fsCourseStore } = React.useContext(MobXProviderContext);
  const theme = useTheme();

  const [search, setSearch] = React.useState(fsCourseStore.filterSearch);
  const [sortby, setSortby] = React.useState(fsCourseStore.filterSortBy);
  const [sortorder, setSortorder] = React.useState(
    fsCourseStore.filterSortOrder
  );

  React.useEffect(() => {
    filterSendThrottle();
  }, [search, sortby, sortorder]);

  const filterSend = () => {
    fsCourseStore.setFilterParams({ search, sortby, sortorder });
  };

  const filterSendThrottle = () => {
    clearTimeout(throttleTO);
    throttleTO = setTimeout(filterSend, 1000);
  };

  const filterSendForce = () => {
    clearTimeout(throttleTO);
    filterSend();
  };

  const onSearch = (e: any) => {
    setSearch(e.target.value);
  };

  const onChangeSort = (option: any) => {
    setSortby(option.target.value);
  };

  const onChangeSortType = () => {
    setSortorder(sortorder === "desc" ? "asc" : "desc");
  };

  const onChangeFilesType = (option: any) => {
    fsCourseStore.setFilterParams({ filestype: option.target.value });
  };

  const stickyStyle = { ...props.style };
  stickyStyle.top = fsStore.isWidget ? 0 : 42;

  const filtersPanel = () => (
    <Box
      p={2}
      className={cx({
        [styles.panel]: true,
        is_stuck: props.isStuck,
        is_not_stuck: !props.isStuck,
        [styles.carded]: fsCourseStore.isFileslistCarded,
        [styles.cardedGroups]:
          fsCourseStore.isFileslistCarded && fsCourseStore.isFileslistGrouped,
      })}
      style={stickyStyle}
      bgcolor="white"
      zIndex={3}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm>
          <TextField
            disabled={fsStore.isLoading}
            fullWidth
            inputProps={{
              "aria-label": tl("Поиск"),
            }}
            InputProps={{
              startAdornment: (
                <MUIAdornment>
                  <button
                    type="submit"
                    aria-label={tl("Поиск")}
                    onClick={filterSendForce}
                  >
                    <SvgIcon type="search" />
                  </button>
                </MUIAdornment>
              ),
              endAdornment:
                !search && search !== 0 ? null : (
                  <MUIAdornment>
                    <button
                      type="reset"
                      aria-label={tl("Очистить")}
                      onClick={() => {
                        if (!fsStore.isLoading) {
                          setSearch("");
                        }
                      }}
                    >
                      <SvgIcon type="search-clear" />
                    </button>
                  </MUIAdornment>
                ),
            }}
            onChange={onSearch}
            placeholder={tl("Поиск")}
            value={search}
            variant="outlined"
          />
        </Grid>
        {fsCourseStore.filterFilesType !== 'transcription' && fsStore.isWidget ? (
          <Grid item xs={5} sm={3}>
            <MUISelect
              variant="outlined"
              fullWidth
              value={fsCourseStore.filterFilesType}
              disabled={fsStore.isLoading}
              onChange={onChangeFilesType}
            >
              {fsCourseStore.filesTypesOptionsList.map(
                (option: { value: string; label: string }, i: number) => (
                  <MenuItem key={`${i}_${option.value}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                )
              )}
            </MUISelect>
          </Grid>
        ) : null}
        <Grid item xs={fsStore.isWidget ? 7 : 12} sm={4}>
          <Grid container spacing={1} wrap="nowrap">
            <Grid item xs zeroMinWidth>
              <MUISelect
                variant="outlined"
                fullWidth
                value={sortby}
                disabled={fsStore.isLoading}
                onChange={onChangeSort}
              >
                {sortOptionsList.map(
                  (option: { value: string; label: string }, i: number) => (
                    <MenuItem key={`${i}_${option.value}`} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )
                )}
                {(fsCourseStore.filterFilesType === "video" ||
                  fsCourseStore.filterFilesType === "audio") && (
                  <MenuItem value="duration">{tl("По длительности")}</MenuItem>
                )}
              </MUISelect>
            </Grid>
            <Grid
              item
              component="button"
              aria-label={tl("Порядок сортировки")}
              onClick={() => {
                if (!fsStore.isLoading) {
                  onChangeSortType();
                }
              }}
              className={styles.sortOrder}
            >
              <SvgIcon type="fs-sort" modifier={sortorder} />
            </Grid>
          </Grid>
        </Grid>
        {props.isStuck ? (
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              fullWidth
              color="primary"
              component={!fsStore.getCheckedFilesCount ? "label" : "span"}
              htmlFor={
                !fsStore.getCheckedFilesCount ? "filestorage-upload" : null
              }
              disabled={
                fsCourseStore.isFileUploadInProgress || fsStore.isLoading
              }
              onClick={
                fsStore.getCheckedFilesCount
                  ? () => {
                      fsStore.switchCheckedFilesWarning();
                    }
                  : null
              }
            >
              {tl("Загрузить файл")}
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );

  return props.isStuck ? <MUIBlock>{filtersPanel()}</MUIBlock> : filtersPanel();
});
